import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    makeStyles,
    Menu,
    MenuItem,
    Typography,
} from '@material-ui/core';
import { blue, green, red } from '@material-ui/core/colors';
import CalendarIcon from '@material-ui/icons/Event';
import AlarmIcon from '@material-ui/icons/Alarm';
import PaidIcon from '@material-ui/icons/Done';
import OverdueIcon from '@material-ui/icons/Warning';
import { Skeleton } from '@material-ui/lab';
import moment from 'moment';
import { JournalEntryList } from '../../../store/modules/accounting/types';
import { displayCurrencyAmount } from '../../../utils/utils';
import TableEmptyState from '../../TableView/TableEmptyState';
import BlockIcon from '@material-ui/icons/Block';
import { MergeTemplate } from 'src/store/modules/application/types';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

type InvoiceListProps = {
    invoices: JournalEntryList;
    loading: boolean;
    onSelect: Function;
    downloadInvoices: Function;
    voidInvoice: Function;
    licenseId?: string;
    personId?: string;
    getMergeTemplatesBegin: Function;
    mergeTemplatesList: MergeTemplate[];
    mergeDocuments: Function;
};

const useStyles = makeStyles((theme) => ({
    card: {
        margin: theme.spacing(4),
    },
    cardText: {
        verticalAlign: 'baseline',
    },
    invoicePaid: {
        color: green[800],
    },
    invoiceOverdue: {
        color: red[700],
    },
    invoiceVoid: {
        color: blue[700],
    },
    invoiceStatus: {
        textAlign: 'center',
    },
    invoiceStatusText: {
        fontWeight: 'bolder',
    },
    divider: {
        margin: theme.spacing(2),
    },
    invoiceAmount: {
        display: 'flex',
        textAlign: 'right',
        fontSize: '1.5em',
        fontWeight: 'bolder',
        color: theme.palette.text.secondary,
        alignItems: 'center',
    },
    skeletonRight: {
        marginLeft: 'auto',
    },
    voidButton: {
        marginTop: 8,
        marginRight: 8,
    },
    downloadBtn: {
        marginRight: 5,
    },
}));

const InvoiceList: FunctionComponent<InvoiceListProps> = (props) => {
    const {
        invoices,
        loading,
        onSelect,
        downloadInvoices,
        voidInvoice,
        licenseId,
        personId,
        getMergeTemplatesBegin,
        mergeTemplatesList,
        mergeDocuments,
    } = props;
    const classes = useStyles();

    const today = moment().startOf('day');

    const [invoiceId, setInvoiceId] = useState<string | undefined>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClickDownload = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleCloseDownload = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleSelect = async (
        event: MouseEvent<HTMLElement>,
        selectedTemplateId: string,
        selectedTemplateName: string,
    ) => {
        event.stopPropagation();
        await mergeDocuments(selectedTemplateId, selectedTemplateName, '', [invoiceId]);
        setAnchorEl(null);
    };

    const handleDownloadInvoices = (
        e: MouseEvent<HTMLElement>,
        invoiceId: string,
        invoiceTypeId: string | undefined,
        fileName: string | undefined,
    ) => {
        e.stopPropagation();
        downloadInvoices(invoiceId, invoiceTypeId, fileName);
    };

    const handleVoidInvoice = (e: MouseEvent<HTMLElement>, invoiceId: string) => {
        e.stopPropagation();
        voidInvoice(invoiceId, licenseId, personId);
    };

    useEffect(() => {
        const payload = {
            where: {
                type: 'Invoice',
            },
            order: ['name ASC'],
            fields: {
                id: true,
                name: true,
                type: true,
            },
        };
        getMergeTemplatesBegin(payload);
    }, [getMergeTemplatesBegin]);

    useEffect(() => {
        setInvoiceId(anchorEl?.id);
    }, [anchorEl, setInvoiceId]);

    return (
        <>
            {loading && (
                <>
                    <Card className={classes.card}>
                        <CardContent>
                            <Skeleton variant="text" width={250} />
                            <br />
                            <Skeleton variant="text" width={200} />
                            <Skeleton variant="text" width={200} />
                            <Divider className={classes.divider} />
                            <Skeleton variant="text" width={100} className={classes.skeletonRight} />
                        </CardContent>
                    </Card>
                    <Card className={classes.card}>
                        <CardContent>
                            <Skeleton variant="text" width={250} />
                            <br />
                            <Skeleton variant="text" width={200} />
                            <Skeleton variant="text" width={200} />
                            <Divider className={classes.divider} />
                            <Skeleton variant="text" width={100} className={classes.skeletonRight} />
                        </CardContent>
                    </Card>
                    <Card className={classes.card}>
                        <CardContent>
                            <Skeleton variant="text" width={250} />
                            <br />
                            <Skeleton variant="text" width={200} />
                            <Skeleton variant="text" width={200} />
                            <Divider className={classes.divider} />
                            <Skeleton variant="text" width={100} className={classes.skeletonRight} />
                        </CardContent>
                    </Card>
                </>
            )}
            {!loading &&
                invoices.map((invoice) => {
                    let statusIcon;
                    let statusClassName;
                    let status;

                    const dueDate = moment(invoice.invoicing?.dueDate ?? '');

                    if (invoice.invoicing?.status === 'Paid') {
                        statusIcon = <PaidIcon fontSize="large" />;
                        statusClassName = classes.invoicePaid;
                        status = 'Paid';
                    } else if (invoice.invoicing?.status === 'Void') {
                        statusIcon = <BlockIcon fontSize="large" />;
                        statusClassName = classes.invoiceVoid;
                        status = 'Void';
                    } else if (dueDate.isValid() && dueDate.isBefore(today)) {
                        statusIcon = <OverdueIcon fontSize="large" />;
                        statusClassName = classes.invoiceOverdue;
                        status = 'Overdue';
                    }

                    return (
                        <Card key={invoice.id} className={classes.card}>
                            <CardActionArea onClick={() => onSelect(invoice)}>
                                <CardHeader
                                    title={`${invoice.invoicing?.type.name} #${invoice.invoicing?.invoiceNumber}`}
                                    action={
                                        invoice.invoicing?.status === 'Open' &&
                                        invoice.invoicing?.amountPaid === 0 && (
                                            <Button
                                                variant="outlined"
                                                color="inherit"
                                                onClick={(e) => handleVoidInvoice(e, invoice.id)}
                                                className={classes.voidButton}
                                            >
                                                VOID
                                            </Button>
                                        )
                                    }
                                />
                                <CardContent>
                                    <Grid container>
                                        <Grid item xs={6}>
                                            <Typography className={classes.cardText}>
                                                <CalendarIcon /> {moment(invoice.date).format('LL')}
                                            </Typography>
                                            <Typography className={classes.cardText}>
                                                {dueDate.isValid() && (
                                                    <>
                                                        <AlarmIcon /> Due {dueDate.format('LL')}
                                                    </>
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} className={`${classes.invoiceStatus} ${statusClassName}`}>
                                            {statusIcon}
                                            <Typography className={classes.invoiceStatusText}>{status}</Typography>
                                        </Grid>
                                    </Grid>
                                    <Divider className={classes.divider} />
                                    <Box display="flex" justifyContent={'space-between'}>
                                        <div>
                                            <Button
                                                className={classes.downloadBtn}
                                                variant="outlined"
                                                color="inherit"
                                                onClick={(e) =>
                                                    handleDownloadInvoices(
                                                        e,
                                                        invoice.id,
                                                        invoice.invoicing?.type.id,
                                                        invoice.invoicing?.type.name,
                                                    )
                                                }
                                            >
                                                DOWNLOAD INVOICE
                                            </Button>
                                            <Button
                                                id={invoice.id}
                                                variant="outlined"
                                                color="inherit"
                                                endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                onClick={handleClickDownload}
                                            >
                                                DOWNLOAD NOTICES
                                            </Button>
                                            <Menu
                                                open={open}
                                                onClose={handleCloseDownload}
                                                onClick={handleCloseDownload}
                                                anchorEl={anchorEl}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'center',
                                                }}
                                                elevation={1}
                                                getContentAnchorEl={null}
                                            >
                                                {mergeTemplatesList.map((item) => (
                                                    <MenuItem
                                                        style={{ fontSize: 14 }}
                                                        key={item.id}
                                                        value={item.name}
                                                        onClick={(e) => handleSelect(e, item.id, item.name)}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                        </div>
                                        <Typography className={classes.invoiceAmount}>
                                            {invoice.invoicing &&
                                                displayCurrencyAmount(invoice.invoicing.amountInvoiced)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            {!loading && invoices.length === 0 && (
                <TableEmptyState title="No invoices" message="New invoices will be displayed here" />
            )}
        </>
    );
};

export default InvoiceList;
